import { useEffect, useRef, useState } from 'react'

import { InfoOutlined, WarningAmber } from '@mui/icons-material'
import { Autocomplete, TextField, Tooltip } from '@mui/material'

import { LoadOption } from '../../../../../../../types'
import { INITIAL_LOAD_FIELD_KEY } from '../../../../../constants'
import { useTexts } from '../useTexts'

type FreeSelectProps = {
  label?: string
  initialOptions: LoadOption[]
  value?: string
  onChange?: (newValue: string) => void
  onBlur?: () => void
  selectedValues: string[]
  error?: boolean
  errorText?: string
  autoFocus?: boolean
}

export default function FreeSelect({
  label,
  initialOptions,
  value: externalValue,
  onChange,
  onBlur,
  selectedValues,
  error,
  errorText,
  autoFocus = false,
}: FreeSelectProps) {
  const [value, setValue] = useState<LoadOption | null>(
    externalValue ? { label: externalValue, value: externalValue } : null,
  )
  const [inputValue, setInputValue] = useState<string>(externalValue || INITIAL_LOAD_FIELD_KEY)
  const selectionMadeRef = useRef(false)

  const texts = useTexts()

  // Sync internal state with external value prop
  useEffect(() => {
    if (externalValue) {
      setValue({ label: externalValue, value: externalValue })
      setInputValue(externalValue)
    } else {
      setValue(null)
      setInputValue(INITIAL_LOAD_FIELD_KEY)
    }
  }, [externalValue])

  // Generate dynamic options based on current input
  const displayOptions =
    inputValue.trim() &&
    !initialOptions.some(opt => opt.label === inputValue) &&
    !selectedValues?.includes(inputValue)
      ? [
          { label: inputValue, value: inputValue },
          ...initialOptions.filter(opt => !selectedValues?.includes(opt.value)),
        ]
      : initialOptions.filter(opt => !selectedValues?.includes(opt.value))

  return (
    <Autocomplete
      freeSolo
      autoHighlight
      value={value}
      inputValue={inputValue}
      options={displayOptions}
      getOptionLabel={option => (typeof option === 'string' ? option : option.label)}
      renderOption={(props, option) => {
        //Check if the option is not in the initial options list
        const isNotInList = !initialOptions.some(opt => opt.label === option.label)
        return (
          <li {...props}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {option.label}
              {isNotInList && (
                <Tooltip title={texts.newLoadWarningTooltip}>
                  <InfoOutlined fontSize="small" color="warning" />
                </Tooltip>
              )}
            </span>
          </li>
        )
      }}
      renderInput={params => {
        //Check if the current value is not in the initial options list
        const isValueNotInList = value && !initialOptions.some(opt => opt.label === value.label)

        return (
          <>
            <TextField
              {...params}
              autoFocus={autoFocus}
              label={label}
              error={error}
              helperText={errorText}
              onBlur={onBlur}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {isValueNotInList && <WarningAmber fontSize="small" color="warning" />}
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          </>
        )
      }}
      onInputChange={(_, newInputValue, reason) => {
        // Update input value based on user input
        if (reason === 'input') {
          setInputValue(newInputValue)
        }

        if (reason === 'clear') {
          setValue(null)
          setInputValue(INITIAL_LOAD_FIELD_KEY)
          onChange?.(INITIAL_LOAD_FIELD_KEY)
        }

        if (reason === 'reset') {
          setInputValue(value?.label || INITIAL_LOAD_FIELD_KEY)
        }
      }}
      onChange={(_, newValue, reason) => {
        // Only trigger onChange when user clicks on an option
        if (reason === 'selectOption') {
          selectionMadeRef.current = true

          if (typeof newValue === 'string') {
            const newOption = { label: newValue, value: newValue }
            setValue(newOption)
            setInputValue(newValue)
            onChange?.(newValue)
          } else if (newValue) {
            setValue(newValue)
            setInputValue(newValue.label)
            onChange?.(newValue.value)
          }
        } else if (reason === 'clear') {
          setValue(null)
          setInputValue(INITIAL_LOAD_FIELD_KEY)
          onChange?.(INITIAL_LOAD_FIELD_KEY)
        }
      }}
      onClose={() => {
        // Reset to previous value if no selection was made
        if (!selectionMadeRef.current) {
          setInputValue(value?.label || INITIAL_LOAD_FIELD_KEY)
        }
        selectionMadeRef.current = false
      }}
    />
  )
}
